import React, { useEffect, useRef } from 'react';
import './styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome ---
import {
    faMapMarkedAlt, faDraftingCompass, faBuilding, faCogs, faCube,
    faProjectDiagram, faClipboardCheck, faLeaf, faGlobeAmericas, faRulerCombined,
    faProcedures, faUsersCog, faChartLine, faStethoscope, faSitemap, faObjectGroup,
    faPencilRuler, faTasks, faSyncAlt, faClipboardList, faLightbulb, faQuoteLeft // Added quote icon
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons

// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => { /* ... (same as before) ... */
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    // Ensure parent exists and children can be accessed before calculating index
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8);
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        // Target prefixed class
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles ---
    const titleObserverOptions = { threshold: 0.3 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions);
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions);
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions);

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    const heroBgImage = 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1920&q=80';

  return (
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            <h1>Hospital Architecture, Planning & Design</h1>
            {/* Added prefixed classes to specific text elements */}
            <p className="cliniqor-sub-heading">
              Cliniqor: Crafting World-Class Healthcare Environments with Insight and Innovation
            </p>
            <p className="cliniqor-intro-text">
              We translate complex healthcare needs into functional, healing spaces that elevate patient care, optimize operations, and stand the test of time.
            </p>
          </div>
        </div>
      </header>

      <section className="cliniqor-highlights-section"> {/* Prefixed */}
        <div className="cliniqor-container">
          <div className="cliniqor-highlights-grid"> {/* Prefixed */}
             {/* Use prefixed animation class */}
            <div className="cliniqor-highlight-item cliniqor-fade-in-up">
              <span className="cliniqor-value">250+</span> <span className="cliniqor-label">Projects Executed</span>
            </div>
            <div className="cliniqor-highlight-item cliniqor-fade-in-up">
              <span className="cliniqor-value">10M+</span> <span className="cliniqor-label">Sq. Ft. Designed</span>
            </div>
            <div className="cliniqor-highlight-item cliniqor-fade-in-up">
              <span className="cliniqor-value">23k+</span> <span className="cliniqor-label">Beds Delivered</span>
            </div>
            <div className="cliniqor-highlight-item cliniqor-fade-in-up">
              <span className="cliniqor-value">100+</span> <span className="cliniqor-label">Domain Experts</span>
            </div>
          </div>
        </div>
      </section>

      <main>
        {/* Use prefixed section classes */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
             <div className="cliniqor-intro-grid">
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     <h3>The Foundation of Effective Healthcare Design</h3>
                     <p>At Cliniqor, we believe hospital design transcends simple construction. It's the art and science of shaping environments that actively contribute to healing, streamline complex operations, and ensure enduring sustainability.</p>
                     <p>Our approach balances critical clinical functionality with architectural distinction, creating spaces that serve patients, staff, and the community.</p>
                 </div>
                 <div className="cliniqor-intro-quote-box cliniqor-fade-in-up">
                     {/* Use FontAwesomeIcon Component */}
                     <span className="cliniqor-quote-icon">
                        <FontAwesomeIcon icon={faQuoteLeft} />
                     </span>
                     <blockquote>
                        Effective hospital planning reduces cost, shortens project timelines, and enhances long-term performance.
                    </blockquote>
                    <p className="cliniqor-quote-source">
                        This principle guides our data-informed methodology, ensuring measurable results from optimized patient flow to regulatory adherence.
                    </p>
                 </div>
             </div>
          </div>
        </section>

        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            {/* Apply prefixed visible class based on observer state */}
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Our Integrated Capabilities</h2>
              <p>A comprehensive suite of services addressing every facet of healthcare infrastructure development, powered by integrated expertise.</p>
            </div>
            <div className="cliniqor-features-grid">
              {/* Use FontAwesomeIcon and prefixed classes */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faMapMarkedAlt} /></div>
                    <h4>Location Analysis & Master Planning</h4> <p>Informed site selection and scalable campus strategies for sustainable growth.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDraftingCompass} /></div>
                    <h4>Space Programming & Planning</h4> <p>Translating clinical needs into optimized layouts enhancing workflow and patient experience.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBuilding} /></div>
                    <h4>Architectural & Structural Design</h4> <p>Creating resilient, functional, and aesthetically inspiring healthcare facilities.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCogs} /></div>
                    <h4>MEP Systems Engineering</h4> <p>Designing critical Mechanical, Electrical, and Plumbing infrastructure for optimal performance.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCube} /></div>
                    <h4>3D Visualization & BIM</h4> <p>Utilizing advanced modeling for clear design communication and coordination.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faProjectDiagram} /></div>
                    <h4>Workflow Optimization</h4> <p>Enhancing operational efficiency through intelligent spatial adjacencies and flow analysis.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faClipboardCheck} /></div>
                    <h4>Regulatory Compliance & Accreditation</h4> <p>Expert navigation of  JCI, and local standards for seamless project approval.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLeaf} /></div>
                    <h4>Sustainable & Green Design</h4> <p>Integrating eco-conscious principles for healthier environments and operational savings.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

         <section className="cliniqor-section cliniqor-section-bg-dark cliniqor-why-choose-us-section">
             <div className="cliniqor-container">
                 <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
                    <h2>The Cliniqor Difference</h2>
                    <p>Proven expertise, measurable results, and a partnership approach define our commitment to excellence in healthcare design.</p>
                </div>
                <div className="cliniqor-why-choose-us-grid">
                     <div className="cliniqor-why-choose-us-item cliniqor-fade-in-up">
                         <span className="cliniqor-icon"><FontAwesomeIcon icon={faGlobeAmericas} /></span> <span className="cliniqor-text"><strong>250+</strong> Global Healthcare Projects Successfully Delivered</span>
                     </div>
                     <div className="cliniqor-why-choose-us-item cliniqor-fade-in-up">
                         <span className="cliniqor-icon"><FontAwesomeIcon icon={faRulerCombined} /></span> <span className="cliniqor-text"><strong>10 Million+</strong> Sq. Ft. of Advanced Hospital Space Designed</span>
                     </div>
                     <div className="cliniqor-why-choose-us-item cliniqor-fade-in-up">
                         <span className="cliniqor-icon"><FontAwesomeIcon icon={faProcedures} /></span> <span className="cliniqor-text"><strong>23,000+</strong> Patient Beds Planned and Implemented</span>
                     </div>
                     <div className="cliniqor-why-choose-us-item cliniqor-fade-in-up">
                         <span className="cliniqor-icon"><FontAwesomeIcon icon={faUsersCog} /></span> <span className="cliniqor-text"><strong>100+</strong> Integrated Multidisciplinary Domain Experts</span>
                     </div>
                     <div className="cliniqor-why-choose-us-item cliniqor-fade-in-up">
                         <span className="cliniqor-icon"><FontAwesomeIcon icon={faChartLine} /></span> <span className="cliniqor-text">Unrivaled Track Record in <strong>Cost & Schedule</strong> Management</span>
                     </div>
                     <div className="cliniqor-why-choose-us-item cliniqor-fade-in-up">
                         <span className="cliniqor-icon"><FontAwesomeIcon icon={faStethoscope} /></span> <span className="cliniqor-text">Deep Expertise Across <strong>All Hospital Types</strong> & Specialties</span>
                     </div>
                </div>
             </div>
         </section>

        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section">
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Methodology: Precision in Planning & Design</h2>
               <p>Our structured process blends clinical insight with architectural innovation to deliver spaces optimized for healing, efficiency, and future adaptability.</p>
            </div>
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSitemap} /></div>
                    <div className="cliniqor-content"><h4>Master Planning & Campus Strategy</h4> <p>Future-proofing healthcare facilities through strategic land use, zoning insight, and scalable infrastructure planning.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faObjectGroup} /></div>
                    <div className="cliniqor-content"><h4>Medical Space Programming</h4> <p>Translating detailed clinical requirements into optimized spatial programs that enhance patient care and operational flow.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faPencilRuler} /></div>
                    <div className="cliniqor-content"><h4>Turnkey Architectural Design</h4> <p>Delivering comprehensive design solutions, from initial concept to detailed construction documentation, specifically for healthcare.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faTasks} /></div>
                    <div className="cliniqor-content"><h4>Design Audits & Compliance Assurance</h4> <p>Conducting rigorous reviews to ensure functional integrity, safety standards, and complete regulatory alignment ( JCI, etc.).</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSyncAlt} /></div>
                    <div className="cliniqor-content"><h4>Facility Renovation & Modernization</h4> <p>Transforming existing healthcare spaces to meet contemporary clinical needs, technological advancements, and patient expectations.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faClipboardList} /></div>
                    <div className="cliniqor-content"><h4>Healthcare Project Management</h4> <p>Providing expert oversight for efficient project execution, ensuring cost-effectiveness, timely delivery, and strategic alignment.</p> </div>
                 </div>
            </div>
          </div>
        </section>

       

      </main>

      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          <h3>Design the Future of Healing with Cliniqor</h3>
          <p>Partner with us to create exceptional healthcare environments that inspire confidence, foster well-being, and define the next generation of patient care. Let's begin the conversation.</p>
          {/* Use prefixed button class */}
          <a href="#contact" className="cliniqor-cta-button">Request a Consultation</a>
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;